/**
 * Pronunciation Progress Floating Panel
 * Collapsible panel showing real-time pronunciation assessment with segment boundaries
 */

import React, { useEffect, useState } from 'react';
import { pronunciationService, SessionAggregateResult } from '../services/pronunciationService';
import { logger } from '../utils/logger';
import { ChevronDownIcon } from './SvgConsolidated/SvgIcons';

interface PronunciationProgressFloatingProps {
  sessionId: string;
  isActive: boolean;
  isExpanded: boolean;
  onToggle: () => void;
  onSegmentAssessed?: (segmentNumber: number, accuracy: number) => void;
  voiceLiveClient?: any; // VoiceLive client for event subscription
}

interface SegmentProgress {
  segment: number;
  accuracy: number;
  status: 'assessed' | 'current' | 'pending';
  wordCount: number;
  errorCount: number;
  referenceText?: string;
  startWordIndex?: number;
  endWordIndex?: number;
}

export const PronunciationProgressFloating: React.FC<PronunciationProgressFloatingProps> = ({
  sessionId,
  isActive,
  isExpanded,
  onToggle,
  onSegmentAssessed,
  voiceLiveClient
}) => {
  const [aggregateData, setAggregateData] = useState<SessionAggregateResult | null>(null);
  const [segments, setSegments] = useState<SegmentProgress[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedSegment, setSelectedSegment] = useState<number | null>(null);

  // Fetch aggregate data via WebSocket events (event-driven) instead of polling
  useEffect(() => {
    if (!sessionId) return;

    const fetchAggregate = async () => {
      try {
        setLoading(true);
        const data = await pronunciationService.getSessionAggregate(sessionId);
        
        if (data.success && data.segments) {
          console.log('[PronunciationProgressFloating] Fetched data:', {
            success: data.success,
            segments_count: data.segments.length,
            segments: data.segments
          });
          setAggregateData(data);
          
          // Convert to segment progress format
          // FILTER OUT segments with null/0 accuracy (background noise assessments that failed)
          const validSegments = data.segments.filter(seg => 
            seg.accuracy !== null && 
            seg.accuracy > 0 && 
            seg.word_count > 0
          );
          console.log('[PronunciationProgressFloating] Valid segments after filter:', validSegments.length);
          
          const progressSegments: SegmentProgress[] = validSegments.map((seg: any, idx) => ({
            segment: seg.segment,
            accuracy: seg.accuracy,
            status: idx === validSegments.length - 1 ? 'current' : 'assessed',
            wordCount: seg.word_count,
            errorCount: seg.error_count,
            referenceText: seg.reference_text,
            startWordIndex: seg.start_word_index,
            endWordIndex: seg.end_word_index
          }));
          
          setSegments(progressSegments);
          
          // Notify parent of new assessment
          if (onSegmentAssessed && data.segments.length > 0) {
            const latestSegment = data.segments[data.segments.length - 1];
            onSegmentAssessed(latestSegment.segment, latestSegment.accuracy);
          }
        }
      } catch (error) {
        logger.logErrorEvent('pronunciation_progress_fetch_failed', error as Error, {
          session_id: sessionId
        });
      } finally {
        setLoading(false);
      }
    };

    // Initial fetch on mount
    fetchAggregate();

    // ================================================================
    // EVENT-DRIVEN UPDATES - Subscribe to WebSocket pronunciation events
    // Eliminates need for polling - backend pushes updates when assessment saved
    // ================================================================
    if (voiceLiveClient) {
      const handlePronunciationUpdate = (eventData: any) => {
        // Check if this update is for our session
        if (eventData?.conversation_id === sessionId && eventData?.data) {
          logger.logVoiceLiveEvent('pronunciation_aggregate_received_via_websocket', {
            session_id: sessionId,
            segments_count: eventData.data.segments?.length || 0
          });
          
          // Update state with pushed data (same logic as initial fetch)
          const data = eventData.data;
          console.log('[PronunciationProgressFloating] WebSocket update received:', {
            conversation_id: eventData.conversation_id,
            success: data.success,
            segments_count: data.segments?.length || 0,
            segments: data.segments
          });
          if (data.success && data.segments) {
            setAggregateData(data);
            
            const validSegments = data.segments.filter((seg: any) => 
              seg.accuracy !== null && 
              seg.accuracy > 0 && 
              seg.word_count > 0
            );
            console.log('[PronunciationProgressFloating] Valid segments after WebSocket filter:', validSegments.length);
            
            const progressSegments: SegmentProgress[] = validSegments.map((seg: any, idx: number) => ({
              segment: seg.segment,
              accuracy: seg.accuracy,
              status: idx === validSegments.length - 1 ? 'current' : 'assessed',
              wordCount: seg.word_count,
              errorCount: seg.error_count,
              referenceText: seg.reference_text,
              startWordIndex: seg.start_word_index,
              endWordIndex: seg.end_word_index
            }));
            
            setSegments(progressSegments);
            
            if (onSegmentAssessed && data.segments.length > 0) {
              const latestSegment = data.segments[data.segments.length - 1];
              onSegmentAssessed(latestSegment.segment, latestSegment.accuracy);
            }
          }
        }
      };

      // Subscribe to pronunciation aggregate updates
      voiceLiveClient.addEventListener('pronunciationAggregateUpdate', handlePronunciationUpdate);

      return () => {
        // Clean up event listener on unmount
        voiceLiveClient.removeEventListener('pronunciationAggregateUpdate', handlePronunciationUpdate);
      };
    }
  }, [sessionId, isActive, onSegmentAssessed, voiceLiveClient]);

  // Don't show panel if no assessment data exists
  // Check aggregateData.segments (raw data) instead of filtered segments
  // This ensures panel shows whenever ANY assessment exists, even if filtering hides some segments
  // console.log('[PronunciationProgressFloating] Render check:', {
  //   hasAggregateData: !!aggregateData,
  //   hasSegments: !!aggregateData?.segments,
  //   segmentsLength: aggregateData?.segments?.length || 0,
  //   willRender: !(!aggregateData || !aggregateData.segments || aggregateData.segments.length === 0)
  // });
  if (!aggregateData || !aggregateData.segments || aggregateData.segments.length === 0) {
    return null;
  }

  const getAccuracyColor = (accuracy: number): string => {
    if (accuracy >= 90) return 'text-green-600';
    if (accuracy >= 75) return 'text-yellow-600';
    if (accuracy >= 60) return 'text-orange-600';
    return 'text-red-600';
  };

  const getAccuracyBgColor = (accuracy: number): string => {
    if (accuracy >= 90) return 'bg-green-100';
    if (accuracy >= 75) return 'bg-yellow-100';
    if (accuracy >= 60) return 'bg-orange-100';
    return 'bg-red-100';
  };

  return (
    <div className="fixed bottom-4 right-4 z-50 max-w-md">
      {/* Floating Panel */}
      <div className="bg-white rounded-lg shadow-2xl border-2 border-blue-200">
        {/* Header - Always Visible */}
        <div 
          className="flex items-center justify-between p-3 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-t-lg cursor-pointer hover:from-blue-100 hover:to-indigo-100 transition-colors"
          onClick={onToggle}
        >
          <div className="flex items-center gap-2">
            <div className={`w-3 h-3 rounded-full ${isActive ? 'bg-blue-500 animate-pulse' : 'bg-gray-400'}`}></div>
            <h3 className="text-sm font-semibold text-gray-800">
              Pronunciation Assessment {!isActive && '(Completed)'}
            </h3>
          </div>
          <div className="flex items-center gap-2">
            {aggregateData?.overall_metrics && (
              <span className={`text-lg font-bold ${getAccuracyColor(aggregateData.overall_metrics.accuracy)}`}>
                {aggregateData.overall_metrics.accuracy.toFixed(0)}%
              </span>
            )}
            <ChevronDownIcon 
              className={`w-5 h-5 text-gray-600 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
            />
          </div>
        </div>

        {/* Expandable Content */}
        {isExpanded && (
          <div className="p-4 max-h-96 overflow-y-auto">
            {!aggregateData ? (
              <div className="text-center text-gray-500 py-4">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto mb-2"></div>
                <p className="text-sm">Waiting for assessment...</p>
              </div>
            ) : (
              <>
                {/* Overall Metrics */}
                {aggregateData.overall_metrics && (
                  <div className="grid grid-cols-3 gap-2 mb-4 p-3 bg-gray-50 rounded">
                    <div className="text-center">
                      <div className={`text-xl font-bold ${getAccuracyColor(aggregateData.overall_metrics.accuracy)}`}>
                        {aggregateData.overall_metrics.accuracy.toFixed(0)}%
                      </div>
                      <div className="text-xs text-gray-500">Accuracy</div>
                    </div>
                    <div className="text-center">
                      <div className={`text-xl font-bold ${getAccuracyColor(aggregateData.overall_metrics.fluency)}`}>
                        {aggregateData.overall_metrics.fluency.toFixed(0)}%
                      </div>
                      <div className="text-xs text-gray-500">Fluency</div>
                    </div>
                    <div className="text-center">
                      <div className="text-xl font-bold text-blue-600">
                        {aggregateData.overall_metrics.total_segments}
                      </div>
                      <div className="text-xs text-gray-500">Segments</div>
                    </div>
                  </div>
                )}

                {/* Progress Improvement */}
                {aggregateData.progress?.improvement !== null && aggregateData.progress?.improvement !== undefined && (
                  <div className="flex items-center justify-center mb-3 p-2 bg-blue-50 rounded text-sm">
                    <span className="text-gray-700">
                      Progress: 
                      <span className={aggregateData.progress.improvement >= 0 ? 'text-green-600 font-semibold ml-1' : 'text-red-600 font-semibold ml-1'}>
                        {aggregateData.progress.improvement >= 0 ? '+' : ''}
                        {aggregateData.progress.improvement.toFixed(1)}%
                      </span>
                    </span>
                  </div>
                )}

                {/* Segment List */}
                <div className="space-y-2 mb-4">
                  <div className="text-xs font-semibold text-gray-700 mb-2">Assessed Segments:</div>
                  {segments.length === 0 ? (
                    <div className="text-center text-gray-500 py-2 text-xs italic">
                      No valid segments to display (segments with 0% accuracy are filtered)
                    </div>
                  ) : (
                    segments.map((segment) => (
                    <div key={segment.segment} className="border border-gray-200 rounded p-2">
                      <div 
                        className="flex items-center gap-2 cursor-pointer"
                        onClick={() => setSelectedSegment(selectedSegment === segment.segment ? null : segment.segment)}
                      >
                        <div className="flex-shrink-0 text-xs font-medium text-gray-600 w-16">
                          Seg {segment.segment}
                        </div>
                        <div className="flex-1">
                          <div className="h-4 bg-gray-200 rounded-full overflow-hidden">
                            <div
                              className={`h-full transition-all duration-500 ${
                                segment.status === 'current' 
                                  ? 'bg-blue-500 animate-pulse' 
                                  : getAccuracyBgColor(segment.accuracy).replace('bg-', 'bg-opacity-70 bg-')
                              }`}
                              style={{ width: `${segment.accuracy}%` }}
                            ></div>
                          </div>
                        </div>
                        <div className={`flex-shrink-0 text-xs font-semibold ${getAccuracyColor(segment.accuracy)} w-12`}>
                          {segment.accuracy.toFixed(0)}%
                        </div>
                        <div className="flex-shrink-0 text-xs text-gray-500">
                          {segment.wordCount}w
                        </div>
                        <ChevronDownIcon 
                          className={`w-4 h-4 text-gray-400 transition-transform ${selectedSegment === segment.segment ? 'rotate-180' : ''}`}
                        />
                      </div>
                      
                      {/* Segment Text Preview */}
                      {selectedSegment === segment.segment && segment.referenceText && (
                        <div className="mt-2 pt-2 border-t border-gray-100">
                          <div className="text-xs text-gray-500 mb-1">
                            Assessed Text 
                            {segment.startWordIndex !== undefined && segment.endWordIndex !== undefined && (
                              <span className="ml-1">(words {segment.startWordIndex}-{segment.endWordIndex})</span>
                            )}:
                          </div>
                          <div className="text-xs text-gray-700 bg-gray-50 p-2 rounded italic">
                            "{segment.referenceText}"
                          </div>
                        </div>
                      )}
                    </div>
                  ))
                  )}
                </div>

                {/* Difficult Words */}
                {aggregateData.mispronunciations && aggregateData.mispronunciations.count > 0 && (
                  <div className="pt-3 border-t border-gray-200">
                    <div className="text-xs font-semibold text-gray-700 mb-2">
                      Words to Practice ({aggregateData.mispronunciations.count}):
                    </div>
                    <div className="flex flex-wrap gap-1">
                      {aggregateData.mispronunciations.words.slice(0, 8).map((word, idx) => (
                        <div
                          key={idx}
                          className="inline-flex items-center gap-1 px-2 py-1 bg-red-50 text-red-700 rounded text-xs"
                          title={`Segment ${word.segment}: ${word.error_type}`}
                        >
                          <span className="font-medium">{word.word}</span>
                          <span className="text-red-500">({word.accuracy.toFixed(0)}%)</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {loading && (
                  <div className="mt-2 text-center text-xs text-gray-400">
                    Updating...
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default PronunciationProgressFloating;
